import { useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { useStateRef } from 'hooks/useStateRef'
import { useEffect, useState } from 'react'
import Styles from './UploadFromDevice.module.scss'
import { ScrollContainer } from 'components/ImagePickerModal/ScrollContainer/ScrollContainer'
import { SelectedImage } from 'types'
import { Portal } from 'components/Portal/Portal'
import { SelectionCount } from 'components/ImagePickerModal/SelectionCount'
import { ConfirmSelectionButton } from 'components/ImagePickerModal/ConfirmSelectionButton'
import { UploadsProps } from '../Uploads/Types'
import { ImageAdd } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { compact } from 'lodash'
import { UPLOAD_IMAGE_MIME_TYPES, UPLOAD_VIDEO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import { MediaFileThumbnail } from 'components/ImagePickerModal/ImageThumbnail/MediaFileThumbnail'

interface UploadFromDeviceProps extends UploadsProps {
  onUploadStart?: (files: File[]) => Promise<void>
}

export const UploadFromDevice = ({ actionsContainerRef, onUploadStart }: UploadFromDeviceProps) => {
  const context = useImagePickerContext()
  const { maxCount, toggleSelectedImage, selectedImages, onConfirm, uploadImages, accept } = context
  const [stagedFiles, setStagedFiles] = useState<SelectedImage[]>([])
  const [fileInputRef, setFileInputRef] = useStateRef<HTMLInputElement | null>(null)

  // sync selectedFiles with selectedImages from the Image Picker Context
  const selectedFiles = compact(selectedImages.map((i) => i.file))

  const handleOnChange = async (files: File[]) => {
    const tempVideos: SelectedImage[] = []
    const tempImages: SelectedImage[] = []
    const totalVideos = files.filter((f) => UPLOAD_VIDEO_MIME_TYPES.includes(f.type)).length
    let videosProcessed = 0

    // Fetch video's duration and then add it to the staged files array
    const fetchVideoDuration = (file: File, videoElement: HTMLVideoElement) => {
      window.URL.revokeObjectURL(videoElement.src)

      let videoToStage: SelectedImage = {
        url: URL.createObjectURL(file),
        file,
        duration: videoElement.duration,
      }
      tempVideos.push(videoToStage)
      videosProcessed++

      // Add to state only once all added videos have been processed
      if (videosProcessed === totalVideos) setStagedFiles((prevStagedFiles) => [...prevStagedFiles, ...tempVideos])
    }

    const newFiles = files.map((file) => {
      let image: SelectedImage = {
        url: URL.createObjectURL(file),
        file: file,
      }

      if (UPLOAD_IMAGE_MIME_TYPES.includes(file.type)) {
        tempImages.push(image)

        return image
      }

      var videoElement = document.createElement('video')
      videoElement.preload = 'metadata'

      videoElement.onloadedmetadata = async () => fetchVideoDuration(file, videoElement)
      videoElement.src = URL.createObjectURL(file)

      // Will be added to the staged files array after loading video metadata and fetching duration
      return undefined
    })
    setStagedFiles((prevStagedFiles) => [...prevStagedFiles, ...compact(newFiles)])
  }

  const handleConfirm = async () => {
    await onUploadStart?.(selectedFiles)
    const uploadedImages = await uploadImages(selectedFiles, context)

    onConfirm(uploadedImages, context)
  }

  // Open file prompt on component mount
  useEffect(() => {
    fileInputRef?.click()
  }, [fileInputRef])

  return (
    <div data-testid="upload-from-device">
      <Portal container={actionsContainerRef}>
        <SelectionCount />
        <ConfirmSelectionButton onPress={handleConfirm} isDisabled={selectedImages.length === 0} />
      </Portal>
      <div className={Styles.container}>
        <ScrollContainer>
          <div className={Styles.grid} key="upload-trigger">
            <div className={Styles.addMedia} onClick={() => fileInputRef?.click()}>
              <ImageAdd />
              <Text variant="smallList" color="var(--dark-80)">
                Add Media
              </Text>
            </div>
            {stagedFiles.map((stagedFile) => (
              <div key={stagedFile.id} className={Styles.item}>
                <MediaFileThumbnail
                  mediaFile={{ ...stagedFile, url: stagedFile.url, mime_type: stagedFile.file?.type }}
                  onToggleSelect={toggleSelectedImage}
                  isSelected={selectedImages.some((i) => i.url === stagedFile.url)}
                  duration={stagedFile.duration}
                />
              </div>
            ))}
          </div>
        </ScrollContainer>
      </div>
      <VisuallyHidden>
        <input
          type="file"
          accept={accept}
          onChange={(e) => {
            const files = Array.from(e.target.files || [])

            handleOnChange(files)
          }}
          ref={setFileInputRef}
          multiple={maxCount > 1}
          name="insert image"
          id="insert image"
          data-testid="file-input"
        />
      </VisuallyHidden>
    </div>
  )
}
